import type { PaymentTerms } from '@/features/settings/business-api';

/* ---------------------------------------------------------------------------
   לוח התשלומים: השלב האחרון ("בסיום העבודה") הוא היתרה — תמיד 100 פחות
   סכום השאר. כך הסכום הכולל נשאר 100% בלי שהמשתמש יחשב. השרת דורש:
   כל שלב 1–100 (שלם), שם לא ריק, ו-1–6 שלבים שמסתכמים ל-100
   (src/modules/tenants/business-settings.ts, paymentTermsSchema).
   --------------------------------------------------------------------------- */

export type Stage = PaymentTerms['schedule'][number];

export const MAX_STAGES = 6;

const clampPercent = (n: number) => Math.min(100, Math.max(0, Math.round(Number.isFinite(n) ? n : 0)));

/** היתרה לשלב האחרון: 100 פחות כל השאר, לא פחות מ-0. שלב יחיד — תמיד 100. */
export function balanceSchedule(schedule: readonly Stage[]): Stage[] {
  if (schedule.length === 0) return [];
  if (schedule.length === 1) return [{ ...schedule[0]!, percent: 100 }];
  const others = schedule.slice(0, -1).map((s) => ({ ...s, percent: clampPercent(s.percent) }));
  const used = others.reduce((sum, s) => sum + s.percent, 0);
  return [...others, { ...schedule[schedule.length - 1]!, percent: Math.max(0, 100 - used) }];
}

/** שינוי אחוז בשלב (לא האחרון) — והשלב האחרון מתעדכן ליתרה. */
export function setStagePercent(schedule: readonly Stage[], index: number, percent: number): Stage[] {
  if (index < 0 || index >= schedule.length - 1) return balanceSchedule(schedule);
  return balanceSchedule(schedule.map((s, i) => (i === index ? { ...s, percent: clampPercent(percent) } : s)));
}

/** הסרת שלב — היתרה עוברת לשלב האחרון. */
export function removeStage(schedule: readonly Stage[], index: number): Stage[] {
  if (schedule.length <= 1) return balanceSchedule(schedule);
  return balanceSchedule(schedule.filter((_, i) => i !== index));
}

/**
 * שלב חדש — לפני האחרון, כדי ש"בסיום" יישאר היתרה. מקבל חצי מהיתרה
 * (לפחות 1%), כך שהלוח נשאר תקין מיד.
 */
export function addStage(schedule: readonly Stage[], label: string): Stage[] {
  if (schedule.length >= MAX_STAGES) return [...schedule];
  if (schedule.length === 0) return [{ label, percent: 100 }];
  const last = schedule[schedule.length - 1]!;
  const share = Math.max(1, Math.floor(last.percent / 2));
  return balanceSchedule([...schedule.slice(0, -1), { label, percent: share }, last]);
}

export const canAddStage = (schedule: readonly Stage[]) =>
  schedule.length < MAX_STAGES && (schedule[schedule.length - 1]?.percent ?? 0) >= 2;

export type StageIssue = 'exceeds' | 'zero' | 'noLabel';
export type ScheduleIssue = 'exceeds' | 'lastZero' | 'zero' | 'noLabel' | 'total';

/** מה לא תקין בשלב מסוים — להצגה ליד השדה. */
export function stageIssue(schedule: readonly Stage[], index: number): StageIssue | null {
  const s = schedule[index];
  if (!s) return null;
  const last = index === schedule.length - 1;
  if (!last && othersTotal(schedule) > 100) return 'exceeds';
  if (s.percent < 1 && !(last && othersTotal(schedule) > 100)) return 'zero';
  if (!s.label.trim()) return 'noLabel';
  return null;
}

/** למה אי אפשר לשמור/לשלוח את הלוח. null — תקין (וזהה למה שהשרת יקבל). */
export function scheduleIssue(schedule: readonly Stage[]): ScheduleIssue | null {
  if (schedule.length === 0) return 'total';
  if (schedule.length > 1 && othersTotal(schedule) > 100) return 'exceeds';
  const last = schedule[schedule.length - 1]!;
  if (schedule.length > 1 && last.percent < 1) return 'lastZero';
  if (schedule.some((s) => s.percent < 1 || s.percent > 100 || !Number.isInteger(s.percent))) return 'zero';
  if (schedule.some((s) => !s.label.trim())) return 'noLabel';
  if (schedule.reduce((sum, s) => sum + s.percent, 0) !== 100) return 'total';
  return null;
}

function othersTotal(schedule: readonly Stage[]): number {
  return schedule.slice(0, -1).reduce((sum, s) => sum + (Number(s.percent) || 0), 0);
}
